// src/pages/ClientDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiRequest } from "../api";
import { ui, uiHandlers } from "../uiStyles";

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [notes, setNotes] = useState([]);
  const [arSummary, setArSummary] = useState(null);

  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [error, setError] = useState("");
  const [archiving, setArchiving] = useState(false);

  function money(v) {
    return `$${Number(v || 0).toFixed(2)}`;
  }

  async function loadAll() {
    setLoading(true);
    setError("");

    try {
      const data = await apiRequest(`/clients/${clientId}/detail`);
      setClient(data.client || null);
      setJobs(data.jobs || []);
      setInvoices(data.invoices || []);
      setNotes(data.notes || []);

      const ar = await apiRequest(`/clients/${clientId}/ar-summary`);
      setArSummary(ar || null);
    } catch (err) {
      console.error("Error loading client detail:", err);
      setError(err.message || "Failed to load client detail");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  async function setArchived(next) {
    setArchiving(true);
    setError("");
    try {
      await apiRequest(`/clients/${clientId}/archive`, {
        method: "PATCH",
        body: { archived: next },
      });
      await loadAll();
    } catch (err) {
      setError(err.message || "Could not update this client.");
    } finally {
      setArchiving(false);
    }
  }
  async function handleAddNote(e) {
    e.preventDefault();
    if (!newNote.trim()) return;

    setSavingNote(true);
    setError("");

    try {
      const created = await apiRequest(`/clients/${clientId}/notes`, {
        method: "POST",
        body: { content: newNote.trim() },
      });

      setNotes((prev) => [created, ...prev]);
      setNewNote("");
    } catch (err) {
      console.error("Error saving note:", err);
      setError(err.message || "Failed to save note");
    } finally {
      setSavingNote(false);
    }
  }

  if (loading) {
    return (
      <div className="app-container">
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
          Loading client...
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="app-container">
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
          <button
            onClick={() => navigate("/clients/management")}
            style={goldOutlineButtonStyle}
            {...uiHandlers.secondaryHover}
          >
            ← Back
          </button>
          <div style={{ marginTop: 16 }}>Client not found.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
        
<div
  style={{
    marginBottom: 20,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  }}
>
  <div>
    <button
      onClick={() => navigate("/clients/management")}
      style={goldOutlineButtonStyle}
      {...uiHandlers.secondaryHover}
    >
      ← Back
    </button>

    <h2 style={{ marginTop: 12, marginBottom: 8 }}>
      Client Detail –{" "}
      {client.company_name || client.contact_name || `Client #${client.id}`}
    </h2>

    <div style={{ color: "#9fb0c3", fontSize: 14 }}>
      {client.email || "—"}{" "}
      {client.phone ? `| ${client.phone}` : ""}
    </div>
  </div>

  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
    {client.archived && (
      <button
        type="button"
        disabled={archiving}
        onClick={() => setArchived(false)}
        style={{
          background: "transparent",
          color: "#ef4444",
          border: "1px solid #ef4444",
          borderRadius: 8,
          padding: "9px 13px",
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "none",
        }}
      >
        {archiving ? "Saving…" : "Unarchive"}
      </button>
    )}
    <button
      onClick={() => navigate(`/clients/${client.id}/edit`)}
      style={goldOutlineButtonStyle}
      {...uiHandlers.secondaryHover}
    >
      Edit Client
    </button>
  </div>
</div>
        {error && (
          <div
            className="card"
            style={{
              marginBottom: 20,
              background: "rgba(120,0,0,0.25)",
              border: "1px solid rgba(255,80,80,0.35)",
              color: "#ffd2d2",
            }}
          >
            {error}
          </div>
        )}

        <div
          className="card"
          style={{
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: 16,
            padding: 20,
            marginBottom: 20,
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            {client.company_name || client.contact_name || "Client"}
          </h3>

          {client.contact_name && (
            <div style={{ marginTop: 6 }}>Contact: {client.contact_name}</div>
          )}
          {client.email && <div style={{ marginTop: 6 }}>Email: {client.email}</div>}
          {client.phone && <div style={{ marginTop: 6 }}>Phone: {client.phone}</div>}
          {client.address && (
            <div
              style={{
                marginTop: 10,
                color: "#9fb0c3",
                whiteSpace: "pre-wrap",
              }}
            >
              {client.address}
            </div>
          )}
        </div>

        <div
          className="card"
          style={{
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: 16,
            padding: 20,
            marginBottom: 20,
          }}
        >
          <h3 style={{ marginTop: 0 }}>A/R Summary</h3>

          {arSummary ? (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <div
                  className="card"
                  style={{
                    marginBottom: 0,
                    padding: 14,
                    background: "#0f2235",
                  }}
                >
                  <div style={{ fontSize: 12, color: "#9fb0c3" }}>Total</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {money(arSummary.total_invoiced)}
                  </div>
                </div>

                <div
                  className="card"
                  style={{
                    marginBottom: 0,
                    padding: 14,
                    background: "#0f2235",
                  }}
                >
                  <div style={{ fontSize: 12, color: "#9fb0c3" }}>Paid</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {money(arSummary.total_paid)}
                  </div>
                </div>

                <div
                  className="card"
                  style={{
                    marginBottom: 0,
                    padding: 14,
                    background: "#0f2235",
                  }}
                >
                  <div style={{ fontSize: 12, color: "#9fb0c3" }}>Outstanding</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {money(arSummary.outstanding)}
                  </div>
                </div>

                <div
                  className="card"
                  style={{
                    marginBottom: 0,
                    padding: 14,
                    background: "#0f2235",
                  }}
                >
                  <div style={{ fontSize: 12, color: "#9fb0c3" }}>Overdue</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {money(arSummary.overdue_outstanding)}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 14, color: "#9fb0c3" }}>
                Aging: {money(arSummary.aging?.current)} /{" "}
                {money(arSummary.aging?.["1_30"])} /{" "}
                {money(arSummary.aging?.["31_60"])} /{" "}
                {money(arSummary.aging?.["61_90"])} /{" "}
                {money(arSummary.aging?.["90_plus"])}
              </div>
            </>
          ) : (
            <div style={{ color: "#9fb0c3" }}>No A/R data available.</div>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            marginBottom: 20,
          }}
        >
          <div
            className="card"
            style={{
              border: "1px solid rgba(255,255,255,0.10)",
              borderRadius: 16,
              padding: 20,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Projects</h3>

            {jobs.length === 0 ? (
              <div style={{ color: "#9fb0c3" }}>No jobs for this client yet.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr
                      key={j.id}
                      onClick={() => navigate(`/jobs/${j.id}`)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{j.id}</td>
                      <td>{j.defendant_name || "(No name)"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div
            className="card"
            style={{
              border: "1px solid rgba(255,255,255,0.10)",
              borderRadius: 16,
              padding: 20,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Invoices</h3>

            {invoices.length === 0 ? (
              <div style={{ color: "#9fb0c3" }}>No invoices for this client yet.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Total</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const total = Number(inv.total || 0);
                    const paid = Number(inv.amount_paid || 0);
                    return (
                      <tr
                        key={inv.id}
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{inv.invoice_number || `#${inv.id}`}</td>
                        <td>${total.toFixed(2)}</td>
                        <td>${(total - paid).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div
          className="card"
          style={{
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: 16,
            padding: 20,
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 14 }}>Notes</h3>

          <form onSubmit={handleAddNote}>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add note..."
              rows={4}
              style={{
                width: "100%",
                background: "#0f2235",
                color: "#ffffff",
                border: "1px solid rgba(255,255,255,0.10)",
                borderRadius: 10,
                padding: 12,
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />

            <div style={{ marginTop: 10 }}>
              <button
                type="submit"
                disabled={savingNote || !newNote.trim()}
                style={{
                  ...goldOutlineButtonStyle,
                  cursor: savingNote ? "default" : "pointer",
                  opacity: savingNote || !newNote.trim() ? 0.7 : 1,
                }}
                {...uiHandlers.secondaryHover}
              >
                {savingNote ? "Saving..." : "Save Note"}
              </button>
            </div>
          </form>

          <div style={{ marginTop: 16 }}>
            {notes.length === 0 ? (
              <div style={{ color: "#9fb0c3" }}>No notes yet.</div>
            ) : (
              notes.map((n) => (
                <div
                  key={n.id}
                  style={{
                    marginBottom: 10,
                    padding: 10,
                    borderRadius: 10,
                    background: "#0f2235",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <strong>{n.user_name || "Staff"}</strong>
                  {n.created_at && (
                    <span style={{ color: "#9fb0c3", fontSize: 12 }}>
                      {" "}
                      • {new Date(n.created_at).toLocaleString()}
                    </span>
                  )}
                  <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
                    {n.content}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const goldOutlineButtonStyle = {
  ...ui.btnSecondary,
  color: "#f5c542",
  border: "1px solid #f5c542",
  background: "rgba(245, 197, 66, 0.08)",
  boxShadow: "0 0 0 1px rgba(245, 197, 66, 0.08)",
};
